import React, { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';

export interface DropdownItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  description?: string;
  disabled?: boolean;
  danger?: boolean;
  divider?: boolean;
}

export interface DropdownProps {
  trigger: React.ReactNode;
  items: DropdownItem[];
  onSelect?: (item: DropdownItem) => void;
  selectedId?: string;
  showCheck?: boolean;
  align?: 'left' | 'right';
  width?: 'auto' | 'full' | number;
  className?: string;
}

export const Dropdown: React.FC<DropdownProps> = ({
  trigger,
  items,
  onSelect,
  selectedId,
  showCheck = false,
  align = 'left',
  width = 'auto',
  className,
}) => {
  return (
    <Menu as="div" className={clsx('relative inline-block text-left', className)}>
      <Menu.Button as={Fragment}>{trigger}</Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className={clsx(
            'absolute z-50 mt-2 rounded-xl bg-white shadow-lg ring-1 ring-black/5',
            'focus:outline-none overflow-hidden',
            'divide-y divide-neutral-100',
            align === 'left' ? 'left-0 origin-top-left' : 'right-0 origin-top-right',
            width === 'full' ? 'w-full' : width === 'auto' ? 'min-w-[200px]' : `w-[${width}px]`
          )}
        >
          <div className="py-1">
            {items.map((item) =>
              item.divider ? (
                <div key={item.id} className="border-t border-neutral-100 my-1" />
              ) : (
                <Menu.Item key={item.id} disabled={item.disabled}>
                  {({ active }) => (
                    <button
                      type="button"
                      onClick={() => onSelect?.(item)}
                      disabled={item.disabled}
                      className={clsx(
                        'w-full flex items-center gap-3 px-4 py-2 text-sm',
                        'transition-colors duration-150',
                        active && !item.disabled && 'bg-neutral-50',
                        item.danger
                          ? 'text-error-600 hover:bg-error-50'
                          : item.disabled
                          ? 'text-neutral-400 cursor-not-allowed'
                          : 'text-neutral-700',
                        selectedId === item.id && 'font-medium'
                      )}
                    >
                      {/* Icon */}
                      {item.icon && (
                        <span className={clsx('w-5 h-5 flex-shrink-0', item.danger && 'text-error-500')}>
                          {item.icon}
                        </span>
                      )}

                      {/* Label & Description */}
                      <div className="flex-1 text-left">
                        <div>{item.label}</div>
                        {item.description && (
                          <div className="text-xs text-neutral-500">{item.description}</div>
                        )}
                      </div>

                      {/* Check mark for selected */}
                      {showCheck && selectedId === item.id && (
                        <CheckIcon className="w-5 h-5 text-primary-600" />
                      )}
                    </button>
                  )}
                </Menu.Item>
              )
            )}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

Dropdown.displayName = 'Dropdown';

// Simple dropdown button with built-in trigger
export interface DropdownButtonProps extends Omit<DropdownProps, 'trigger'> {
  label: string;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
}

export const DropdownButton: React.FC<DropdownButtonProps> = ({
  label,
  variant = 'secondary',
  size = 'md',
  ...props
}) => {
  const variantStyles = {
    primary: 'bg-primary-600 text-white hover:bg-primary-700',
    secondary: 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
    outline: 'border border-neutral-300 text-neutral-700 hover:bg-neutral-50',
    ghost: 'text-neutral-600 hover:bg-neutral-100',
  };

  const sizeStyles = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-base',
  };

  return (
    <Dropdown
      {...props}
      trigger={
        <button
          type="button"
          className={clsx(
            'inline-flex items-center gap-2 rounded-lg font-medium',
            'transition-colors duration-200',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
            variantStyles[variant],
            sizeStyles[size]
          )}
        >
          {label}
          <ChevronDownIcon className="w-4 h-4" />
        </button>
      }
    />
  );
};

DropdownButton.displayName = 'DropdownButton';

export default Dropdown;

